import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string; // 50–60 chars
  description: string; // 150–160 chars
  canonical?: string;
  keywords?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  twitterImage?: string;
  siteName?: string;
  ldjson?: object | object[]; // schema.org - can be single object or array
  noIndex?: boolean; // For pages that shouldn't be indexed
  alternateLanguages?: { hreflang: string; href: string }[];
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  keywords,
  ogType = 'website',
  ogImage = 'https://rossopomodoro.is/images/hero11.jpg',
  twitterImage,
  siteName = 'Rossopomodoro Reykjavik',
  ldjson,
  noIndex = false,
  alternateLanguages = [
    { hreflang: 'x-default', href: canonical || '' },
    { hreflang: 'en', href: canonical || '' },
    { hreflang: 'is', href: canonical || '' },
  ],
}) => {
  // Build canonical URL from environment if not provided
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://rossopomodoro.is';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const canonicalUrl = canonical || `${siteUrl}${currentPath}`;

  // Build alternate language URLs
  const alternateUrls = alternateLanguages.map(alt => ({
    ...alt,
    href: alt.href || canonicalUrl,
  }));

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noIndex ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
      />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={twitterImage || ogImage} />

      {/* Alternate language tags */}
      {alternateUrls.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" href={href} hreflang={hreflang} />
      ))}

      {/* Structured Data */}
      {ldjson &&
        (Array.isArray(ldjson) ? (
          ldjson.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">{JSON.stringify(ldjson)}</script>
        ))}
    </Helmet>
  );
};

export default SEO;
